/**
 * Which links the user opens from a Chat answer, and how long they then stay
 * away at one (docs/services/workflow_orchestrator_service/features/lookup.md).
 * Analytics only: a link behaves exactly as it does without this, and nothing
 * is shown.
 *
 * A click or middle-click on an http(s) link in an answer's text is reported
 * at once, with the block whose headline or text held it (the closest
 * `data-answer-block`), or null when the answer is shown whole. If the page
 * then goes hidden within five seconds, the user is taken to be away at that
 * link, and their return is reported with the whole seconds the page was
 * hidden. Only the latest click waits for a return; a page that never goes
 * hidden, or never comes back, reports none.
 */

import type React from 'react';
import { useEffect, useRef } from 'react';

/** How soon after a click the page must go hidden to count as away at that link. */
export const AWAY_WITHIN_MS = 5000;

/** `awaySeconds` absent: the click; present: the return after it. */
export type LinkRecorder = (turnId: string, block: number | null, url: string, awaySeconds?: number) => void;

interface Clicked {
    turnId: string;
    block: number | null;
    url: string;
    at: number;
    /** When the page went hidden after the click; unset until it does. */
    hiddenAt?: number;
}

const ABSOLUTE_HTTP = /^https?:\/\/\S+$/;

/** The link's address as the answer wrote it, so it matches the answer's
 *  `[label](url)` exactly: the original of a rewritten link
 *  (`data-answer-href`, set by ConceptLink), else the href as written when
 *  that is absolute, else (a relative link) as the browser resolved it.
 *  Null for a link that is not http(s). */
function linkUrl(anchor: HTMLAnchorElement): string | null {
    if (anchor.protocol !== 'http:' && anchor.protocol !== 'https:') return null;
    const original = anchor.getAttribute('data-answer-href') ?? '';
    if (ABSOLUTE_HTTP.test(original)) return original;
    const written = anchor.getAttribute('href') ?? '';
    return ABSOLUTE_HTTP.test(written) ? written : anchor.href;
}

/** Returns, per answer, the mouse handlers for the element that holds it;
 *  undefined for an answer without a turn id, which records nothing. */
export function useAnswerLinkClicks(record: LinkRecorder) {
    const latest = useRef<Clicked | null>(null);
    const recordNow = useRef(record);
    recordNow.current = record;

    useEffect(() => {
        const visibility = () => {
            const clicked = latest.current;
            if (!clicked) return;
            const now = Date.now();
            if (document.visibilityState === 'hidden') {
                if (clicked.hiddenAt !== undefined) return;
                if (now - clicked.at <= AWAY_WITHIN_MS) clicked.hiddenAt = now;
                else latest.current = null;
            } else if (document.visibilityState === 'visible' && clicked.hiddenAt !== undefined) {
                latest.current = null;
                recordNow.current(clicked.turnId, clicked.block, clicked.url, Math.round((now - clicked.hiddenAt) / 1000));
            }
        };
        document.addEventListener('visibilitychange', visibility);
        return () => {
            document.removeEventListener('visibilitychange', visibility);
            latest.current = null;
        };
    }, []);

    return (turnId?: string) => {
        if (!turnId) return undefined;
        // Capture: a link in an answer stops its click from bubbling (so a
        // headline's link does not toggle its block). Only the answer's own
        // text counts: not the chrome under it (`data-answer-chrome`), nor a
        // definition preview, which opens in a portal outside the element.
        const clicked = (event: React.MouseEvent<HTMLElement>) => {
            const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
            if (!(anchor instanceof HTMLAnchorElement) || !event.currentTarget.contains(anchor) || anchor.closest('[data-answer-chrome]')) return;
            const url = linkUrl(anchor);
            if (!url) return;
            const marked = anchor.closest('[data-answer-block]');
            const block = marked ? Number(marked.getAttribute('data-answer-block')) : null;
            latest.current = { turnId, block, url, at: Date.now() };
            recordNow.current(turnId, block, url);
        };
        return {
            onClickCapture: clicked,
            // Middle-click only: other buttons do not open the link.
            onAuxClickCapture: (event: React.MouseEvent<HTMLElement>) => { if (event.button === 1) clicked(event); },
        };
    };
}
